import { animate, set, stagger, cubicBezier } from "animejs";

/**
 * デバイス判定
 *
 * @param {String} device - 'pc' または 'sp'
 * @return {boolean}
 */
export const mq = (device) => {
  const isSP = window.matchMedia("(max-width: 768.9px)").matches;
  const isPC = window.matchMedia("(min-width: 769px)").matches;

  if (device === "sp") {
    return isSP;
  }
  if (device === "pc") {
    return isPC;
  }
  return false; // 不明な引数が渡された場合
};

/**
 * ページトップへスクロール - Vanilla JavaScript
 *
 * @param {string} val
 */
export const goToPageTop = (val) => {
  const elements = document.querySelectorAll(val);
  elements.forEach((element) => {
    element.addEventListener("click", (event) => {
      event.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  });
};

/**
 * スムーススクロール - Vanilla JavaScript
 *
 * @param {string} val
 */
export const smoothScroll = (val) => {
  const elements = document.querySelectorAll(val);
  elements.forEach((element) => {
    element.addEventListener("click", (event) => {
      event.preventDefault();

      const href = element.getAttribute("href");
      const targetSelector = href === "#" || href === "" ? "html" : href;
      const target = targetSelector === "html" ? document.documentElement : document.querySelector(targetSelector);

      if (target) {
        const position = target.offsetTop; // ターゲット要素のトップ位置を取得
        window.scrollTo({
          top: position,
          behavior: "smooth", // スムーズスクロールを有効化
        });
      }
    });
  });
};

/**
 * アコーディオン処理 - Vanilla JavaScript
 *
 * @param {string} val
 */
export const showAccordion = (val) => {
  const elements = document.querySelectorAll(val);

  // 初期状態で次の要素を非表示にする
  elements.forEach((element) => {
    const nextSibling = element.nextElementSibling;
    if (nextSibling) {
      nextSibling.style.display = "none";
    }

    // クリックイベントをバインド
    element.addEventListener("click", () => {
      const isOpen = element.classList.contains("show-accordion");
      if (isOpen) {
        element.classList.remove("show-accordion");
        if (nextSibling) {
          nextSibling.style.display = "none"; // アコーディオンを閉じる
        }
      } else {
        element.classList.add("show-accordion");
        if (nextSibling) {
          nextSibling.style.display = "block"; // アコーディオンを開く
        }
      }
    });
  });
};

/**
 * アコーディオン処理2 - Vanilla JavaScript
 *
 * @param {string} val
 */
export const showAllAccordion = (val) => {
  const headers = document.querySelectorAll(`${val} dt`);

  headers.forEach((header) => {
    header.addEventListener("click", (event) => {
      const tag = event.currentTarget;
      const dl = tag.closest("dl");
      const dds = dl.querySelectorAll("dd");

      if (tag.classList.contains("show")) {
        // すでに開いている場合
        tag.classList.remove("show");
        dds.forEach((dd) => (dd.style.display = "none")); // アコーディオンを閉じる
      } else {
        // 常に一つだけ開ける場合
        headers.forEach((h) => h.classList.remove("show")); // 他の開いているものを閉じる
        const allDds = document.querySelectorAll(`${val} dd`);
        allDds.forEach((dd) => (dd.style.display = "none")); // 他の開いているddを閉じる

        // 現在の要素を開く
        tag.classList.add("show");
        const nextDd = tag.nextElementSibling;
        if (nextDd && nextDd.tagName === "DD") {
          nextDd.style.display = "block";
        }
      }
    });
  });
};


/**
 * タブ切り替え初期化 - すべての.js-sub-tabsを自動検出して初期化
 * anime.jsを使用したフェードイン+移動アニメーション付き
 */
export const initTabs = () => {
  const allTabContainers = document.querySelectorAll(".js-sub-tabs");
  if (!allTabContainers.length) return;

  const CARD_SELECTOR = ".event-card";
  const ease = cubicBezier(0.1, 0.7, 0.5, 1);

  /** display:none 内でも画像を先読み・デコードする */
  const preloadImages = (root) => {
    const images = [...root.querySelectorAll("img")];
    return Promise.all(
      images.map((img) => {
        const src = img.currentSrc || img.src;
        if (!src) return Promise.resolve();

        const load = new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const preloader = new Image();
          preloader.onload = () => resolve();
          preloader.onerror = () => resolve();
          preloader.src = src;
        });

        return load.then(() => (img.decode ? img.decode().catch(() => {}) : undefined));
      }),
    );
  };

  const waitImagesReady = (root) => {
    const images = [...root.querySelectorAll("img")];
    return Promise.all(
      images.map((img) => {
        if (img.complete && img.naturalWidth > 0) {
          return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
        }
        return new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        }).then(() => (img.decode ? img.decode().catch(() => {}) : undefined));
      }),
    );
  };

  allTabContainers.forEach((tabsContainer) => {
    // 隣接する.js-sub-contentを取得
    const contentContainer = tabsContainer.nextElementSibling;

    if (!contentContainer?.classList.contains("js-sub-content")) return;

    const tabs = Array.from(tabsContainer.children);

    // SimpleBar適用後は元の子要素が .simplebar-content 配下へ移動している
    const panelHost = contentContainer.querySelector(":scope > .simplebar-wrapper .simplebar-content") ?? contentContainer;
    const contents = Array.from(panelHost.children);

    /** date-panels 内のスクロール要素（SimpleBar 利用時は content-wrapper） */
    const getScrollElement = () =>
      contentContainer.querySelector(":scope > .simplebar-wrapper .simplebar-content-wrapper") ?? contentContainer;

    /** タブ切替時にパネル内スクロールを先頭へ滑らかに戻す */
    const scrollPanelsToTop = () => {
      const scrollEl = getScrollElement();
      if (!scrollEl || scrollEl.scrollTop === 0) return;
      scrollEl.scrollTo({ top: 0, behavior: "smooth" });
    };

    // 非表示パネルの画像も事前にキャッシュしておく（初回切替のちらつき防止）
    preloadImages(contentContainer);

    // 初期表示（最初のタブを表示）
    tabs[0]?.classList.add("show-tab");
    contents.forEach((child, i) => {
      if (i === 0) {
        child.style.display = "block";
        child.classList.add("show-tab-child");
        set(child.querySelectorAll(CARD_SELECTOR), {
          opacity: 1,
          translateY: 0,
        });
      } else {
        child.style.display = "none";
        child.classList.remove("show-tab-child");
      }
    });

    // タブクリックイベント
    tabs.forEach((tab, index) => {
      if (tab.dataset.tabInitialized === "true") return;
      tab.dataset.tabInitialized = "true";

      tab.addEventListener("click", () => {
        if (tab.classList.contains("show-tab")) return;

        scrollPanelsToTop();

        const currentContent = contents.find((child) => child.classList.contains("show-tab-child"));
        const nextContent = contents[index];

        tabs.forEach((item) => item.classList.remove("show-tab"));
        tab.classList.add("show-tab");

        const showNextContent = async (content) => {
          content.classList.add("show-tab-child");
          content.style.display = "block";

          const animationTargets = content.querySelectorAll(CARD_SELECTOR);
          if (!animationTargets.length) return;

          // ロード待ち中に見えるのを防ぐ
          set(animationTargets, {
            opacity: 0,
            translateY: 20,
          });

          // 初回表示前に画像のデコード完了を待つ
          await waitImagesReady(content);

          requestAnimationFrame(() => {
            animate(animationTargets, {
              opacity: [0, 1],
              translateY: [20, 0],
              duration: 600,
              ease,
              delay: stagger(20, { start: 0 }),
            });
          });
        };

        if (currentContent && currentContent !== nextContent) {
          const currentCards = currentContent.querySelectorAll(CARD_SELECTOR);

          if (!currentCards.length) {
            currentContent.style.display = "none";
            currentContent.classList.remove("show-tab-child");
            showNextContent(nextContent);
            return;
          }

          animate(currentCards, {
            opacity: [1, 0],
            translateY: [0, 20],
            duration: 200,
            ease,
            delay: stagger(20, { start: 0 }),
            onComplete: () => {
              currentContent.style.display = "none";
              currentContent.classList.remove("show-tab-child");
              showNextContent(nextContent);
            },
          });
        } else {
          showNextContent(nextContent);
        }
      });
    });
  });
};

// CSSクラスを使用してトランジションを管理
const TRANSITION_PROPERTIES = "height, margin, padding";
const TRANSITION_DURATION = "300ms";
const TRANSITION_TIMING_FUNCTION = "ease";

const addTransition = (el) => {
  el.style.transitionProperty = TRANSITION_PROPERTIES;
  el.style.transitionDuration = TRANSITION_DURATION;
  el.style.transitionTimingFunction = TRANSITION_TIMING_FUNCTION;
  el.style.willChange = "height, margin, padding"; // 最適化のヒント
};

const removeTransition = (el) => {
  el.style.transitionProperty = "";
  el.style.transitionDuration = "";
  el.style.transitionTimingFunction = "";
  el.style.willChange = "";
};

export const slideUp = (el, duration = 300) => {
  el.style.height = `${el.offsetHeight}px`;
  el.style.overflow = "hidden";
  addTransition(el);

  requestAnimationFrame(() => {
    el.style.height = "0";
    el.style.paddingTop = "0";
    el.style.paddingBottom = "0";
    el.style.marginTop = "0";
    el.style.marginBottom = "0";
  });

  const handleTransitionEnd = () => {
    el.style.display = "none";
    el.classList.remove("is-open");
    removeTransition(el);
    el.removeEventListener("transitionend", handleTransitionEnd);
  };

  el.addEventListener("transitionend", handleTransitionEnd);
};

export const slideDown = (el, duration = 300, flex = false) => {
  el.classList.add("is-open");
  el.style.removeProperty("display");

  let display = window.getComputedStyle(el).display;
  if (display === "none") {
    display = flex ? "flex" : "block";
  }
  el.style.display = display;

  const height = el.scrollHeight; // offsetHeightよりscrollHeightの方が適切
  el.style.overflow = "hidden";
  el.style.height = "0";
  el.style.paddingTop = "0";
  el.style.paddingBottom = "0";
  el.style.marginTop = "0";
  el.style.marginBottom = "0";
  addTransition(el);

  requestAnimationFrame(() => {
    el.style.height = `${height}px`;
    el.style.removeProperty("padding-top");
    el.style.removeProperty("padding-bottom");
    el.style.removeProperty("margin-top");
    el.style.removeProperty("margin-bottom");
  });

  const handleTransitionEnd = () => {
    el.style.removeProperty("height");
    el.style.removeProperty("overflow");
    removeTransition(el);
    el.removeEventListener("transitionend", handleTransitionEnd);
  };

  el.addEventListener("transitionend", handleTransitionEnd);
};

export const slideToggle = (el, duration = 300, flex = false) => {
  if (window.getComputedStyle(el).display === "none") {
    return slideDown(el, duration, flex);
  } else {
    return slideUp(el, duration);
  }
};

/**
 * anime.js v4を使ったハイクオリティなアコーディオン
 * @param {HTMLElement} elem - アコーディオンを含む親要素
 */
export const accordionsFunc = async (elem) => {
  if (!elem) return;

  const accordions = elem.querySelectorAll(".js-accordion");
  if (!accordions.length) return;

  // anime.js v4をインポート
  const { animate } = await import("animejs");

  accordions.forEach((accordion) => {
    const trigger = accordion.querySelector(".accordion-title");
    const content = accordion.querySelector(".accordion-detail");
    const title = trigger?.querySelector(".title");

    if (!trigger || !content || !title) return;

    // すでに初期化済みの場合はスキップ
    if (trigger.dataset.accordionInitialized === "true") return;
    trigger.dataset.accordionInitialized = "true";

    // 初期状態の設定（.is-openクラスがある場合は開いた状態）
    const isInitiallyOpen = accordion.classList.contains("is-open");

    if (isInitiallyOpen) {
      trigger.classList.add("show");
      content.style.display = "flex";
      content.style.height = "";
      content.style.opacity = "";
      content.style.overflow = "";
    } else {
      content.style.display = "none";
      content.style.height = "0px";
      content.style.opacity = "0";
      content.style.overflow = "hidden";
    }

    // クリックイベント
    title.addEventListener("click", (e) => {
      // リンクの場合はアコーディオン開閉しない
      if (trigger.tagName === "A") {
        const href = trigger.getAttribute("href");
        if (href && href !== "#") return;
        e.preventDefault();
        return;
      }
      e.preventDefault();

      const isOpen = trigger.classList.contains("show");

      if (isOpen) {
        // 閉じる
        trigger.classList.remove("show");
        content.style.overflow = "hidden";

        animate(content, {
          height: [content.offsetHeight, 0],
          opacity: [1, 0],
          duration: 400,  // 閉じる速度（ミリ秒）


        }, {
          ease: "inOut(cubic)",
          onComplete: () => {
            content.style.display = "none";
            content.style.overflow = "";
          }
        });
      } else {
        // 他の開いているアコーディオンを閉じる
        accordions.forEach((otherAccordion) => {
          if (otherAccordion === accordion) return; // 自分自身はスキップ

          const otherTrigger = otherAccordion.querySelector(".accordion-title");
          const otherContent = otherAccordion.querySelector(".accordion-detail");

          if (otherTrigger?.classList.contains("show")) {
            otherTrigger.classList.remove("show");
            otherContent.style.overflow = "hidden";

            animate(otherContent, {
              height: [otherContent.offsetHeight, 0],
              opacity: [1, 0],
              duration: 250,
            }, {
              ease: "inOut(cubic)",
              onComplete: () => {
                otherContent.style.display = "none";
                otherContent.style.overflow = "";
              }
            });
          }
        });

        // 開く
        trigger.classList.add("show");
        content.style.display = "flex";
        content.style.overflow = "hidden";
        content.style.height = "0px";
        content.style.opacity = "0";

        // 高さを計算
        const endHeight = content.scrollHeight;

        animate(content, {
          height: [0, endHeight],
          opacity: [0, 1],
          duration: 400,  // 開く速度（ミリ秒）
        }, {

          ease: "out(cubic)",
          onComplete: () => {
            content.style.height = "";
            content.style.overflow = "";
          }
        });
      }
    });
  });
};

/**
 * 月の数字を英語の3文字略称に変換する関数
 * @param {number} monthNumber - 1から12の整数
 * @returns {string} 英語の3文字略称（例："FEB"）
 * @throws {Error} 無効な月の数字が渡された場合
 */
export const getMonthAbbreviation = (monthNumber) => {
  const monthAbbreviations = [
    "JAN", // 1
    "FEB", // 2
    "MAR", // 3
    "APR", // 4
    "MAY", // 5
    "JUN", // 6
    "JUL", // 7
    "AUG", // 8
    "SEP", // 9
    "OCT", // 10
    "NOV", // 11
    "DEC", // 12
  ];

  if (typeof monthNumber !== "number" || !Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
    throw new Error("有効な月の数字（1〜12）を入力してください。");
  }

  // 配列は0から始まるため、月番号から1を引く
  return monthAbbreviations[monthNumber - 1];
};

/**
 * スマホ表示時に .ticket-btn を main要素の後（footer の上）に移動する処理
 * 画面サイズが変わっても正しく動作し、高負荷にならないように最適化
 */
export const initTicketBtnPosition = () => {
  const ticketBtn = document.querySelector(".ticket-btn");
  if (!ticketBtn) return;

  // 元の親要素を記憶（PC時に戻すため）
  const originalParent = ticketBtn.parentElement;
  const nextSibling = ticketBtn.nextElementSibling;

  // 移動先の要素を取得（footer があればその前、なければ main の次）
  const footer = document.querySelector("footer");
  const mainElement = document.querySelector("main");

  // 現在の状態を保持（不要な移動を防ぐ）
  let currentState = null; // 'sp' | 'pc' | null

  /**
   * デバイスに応じて要素を移動する処理
   * @param {boolean} isSP - スマホ表示かどうか
   */
  const moveElement = (isSP) => {
    // 状態が変わらない場合は何もしない（パフォーマンス最適化）
    const newState = isSP ? "sp" : "pc";
    if (currentState === newState) return;

    if (isSP) {
      // スマホ時: footer の直前（または main の直後）に移動
      if (footer) {
        // footer が存在する場合はその直前に挿入
        footer.parentNode.insertBefore(ticketBtn, footer);
      } else if (mainElement && mainElement.nextSibling) {
        // footer がない場合は main の次の兄弟要素の前に挿入
        mainElement.parentNode.insertBefore(ticketBtn, mainElement.nextSibling);
      } else if (mainElement) {
        // main の次に兄弟要素がない場合は親要素の最後に追加
        mainElement.parentNode.appendChild(ticketBtn);
      } else {
        // main も見つからない場合は body の最後に追加（フォールバック）
        document.body.appendChild(ticketBtn);
      }
    } else {
      // PC時: 元の位置に戻す
      if (nextSibling) {
        originalParent.insertBefore(ticketBtn, nextSibling);
      } else {
        originalParent.appendChild(ticketBtn);
      }
    }

    currentState = newState;
  };

  // matchMediaを使用してブレークポイントを監視
  const mediaQuery = window.matchMedia("(max-width: 768.9px)");

  /**
   * メディアクエリの変更を処理
   * @param {MediaQueryListEvent|MediaQueryList} e - メディアクエリイベント
   */
  const handleMediaChange = (e) => {
    moveElement(e.matches);
  };

  // 初期実行
  handleMediaChange(mediaQuery);

  // メディアクエリの変更を監視（画面サイズ変更に対応）
  // addEventListener がサポートされている場合はそれを使用（モダンブラウザ）
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handleMediaChange);
  } else {
    // 古いブラウザ向けのフォールバック
    mediaQuery.addListener(handleMediaChange);
  }

  // クリーンアップ関数を返す（必要に応じて後で使用可能）
  return () => {
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener("change", handleMediaChange);
    } else {
      mediaQuery.removeListener(handleMediaChange);
    }
  };
};
